import React, { useState, useEffect } from 'react';
import {
  Card, Button, Modal, Badge, Form, Row, Col, Alert,
  Spinner, OverlayTrigger, Tooltip, Tabs, Tab, Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRoute, faInfoCircle, faTrash, faPlus, faCheck,
  faFlask, faSave, faDownload, faTimesCircle, faServer,
  faArrowRight, faCheckCircle, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';
import { toast } from 'react-toastify';

const RouteBuilder = ({ projectId, onSave, onCancel, existingRoute = null }) => {
  const [redirectors, setRedirectors] = useState([]);
  const [listeners, setListeners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showTestResults, setShowTestResults] = useState(false);

  // Route configuration
  const [routeName, setRouteName] = useState('');
  const [activeTab, setActiveTab] = useState('primary');
  const [routes, setRoutes] = useState({
    primary: {
      priority: 1,
      enabled: true,
      hops: [], // Array of redirector objects
      config: {
        maxRetries: 3,
        timeout: 10000, // milliseconds
        jitter: 20 // percentage
      }
    },
    fallback1: {
      priority: 2,
      enabled: false,
      hops: [],
      config: {
        maxRetries: 3,
        timeout: 10000,
        jitter: 20
      }
    },
    fallback2: {
      priority: 3,
      enabled: false,
      hops: [],
      config: {
        maxRetries: 3,
        timeout: 10000,
        jitter: 20
      }
    },
    fallback3: {
      priority: 4,
      enabled: false,
      hops: [],
      config: {
        maxRetries: 3,
        timeout: 10000,
        jitter: 20
      }
    }
  });

  // Drag state
  const [draggedRedirector, setDraggedRedirector] = useState(null);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  useEffect(() => {
    if (existingRoute) {
      loadExistingRoute(existingRoute);
    }
  }, [existingRoute]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [redirectorsRes, listenersRes] = await Promise.all([
        c2Service.getRedirectors({ projectId, isOnline: true }),
        c2Service.getListeners()
      ]);

      setRedirectors(redirectorsRes.data.data || []);
      setListeners(listenersRes.data.data.filter(l => l.status === 'active') || []);
    } catch (error) {
      console.error('Error fetching route builder data:', error);
      toast.error('Failed to load redirectors and listeners');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingRoute = (route) => {
    setRouteName(route.name);
    // Load route configurations from existing route
    // This would map the saved route structure to our state
  };

  // Drag handlers
  const handleDragStart = (e, redirector) => {
    setDraggedRedirector(redirector);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, routeKey, hopIndex) => {
    e.preventDefault();
    if (!draggedRedirector) return;

    const updatedRoute = { ...routes[routeKey] };
    const newHops = [...updatedRoute.hops];

    // Insert or replace at the hop index
    if (hopIndex >= newHops.length) {
      newHops.push(draggedRedirector);
    } else {
      newHops[hopIndex] = draggedRedirector;
    }

    updatedRoute.hops = newHops;
    setRoutes({ ...routes, [routeKey]: updatedRoute });
    setDraggedRedirector(null);
  };

  const handleRemoveHop = (routeKey, hopIndex) => {
    const updatedRoute = { ...routes[routeKey] };
    updatedRoute.hops = updatedRoute.hops.filter((_, idx) => idx !== hopIndex);
    setRoutes({ ...routes, [routeKey]: updatedRoute });
  };

  const handleClearRoute = (routeKey) => {
    const updatedRoute = { ...routes[routeKey] };
    updatedRoute.hops = [];
    setRoutes({ ...routes, [routeKey]: updatedRoute });
  };

  const handleConfigChange = (routeKey, field, value) => {
    const updatedRoute = { ...routes[routeKey] };
    updatedRoute.config[field] = value;
    setRoutes({ ...routes, [routeKey]: updatedRoute });
  };

  const handleToggleRoute = (routeKey) => {
    const updatedRoute = { ...routes[routeKey] };
    updatedRoute.enabled = !updatedRoute.enabled;
    setRoutes({ ...routes, [routeKey]: updatedRoute });
  };

  const handleTestAllRoutes = async () => {
    if (!routeName) {
      toast.error('Please enter a route name first');
      return;
    }

    setTesting(true);
    const results = [];

    for (const [key, route] of Object.entries(routes)) {
      if (!route.enabled || route.hops.length === 0) {
        results.push({
          routeKey: key,
          priority: route.priority,
          enabled: route.enabled,
          skipped: true,
          reason: route.hops.length === 0 ? 'No hops configured' : 'Route disabled'
        });
        continue;
      }

      const routeResult = {
        routeKey: key,
        priority: route.priority,
        enabled: route.enabled,
        hops: route.hops,
        hopResults: [],
        totalLatency: 0,
        success: true,
        error: null
      };

      // Test each hop sequentially
      for (const hop of route.hops) {
        try {
          const result = await c2Service.testRedirector(hop._id);
          const hopResult = {
            redirector: hop,
            success: result.data.success,
            latency: result.data.data?.latency || 0,
            responseTime: result.data.data?.responseTime || 0,
            error: result.data.error || null
          };

          routeResult.hopResults.push(hopResult);
          routeResult.totalLatency += hopResult.latency || 0;

          if (!hopResult.success) {
            routeResult.success = false;
            routeResult.error = `Hop ${hop.name} failed: ${hopResult.error}`;
            break; // Stop testing this route if a hop fails
          }
        } catch (error) {
          routeResult.success = false;
          routeResult.error = `Failed to test ${hop.name}: ${error.message}`;
          routeResult.hopResults.push({
            redirector: hop,
            success: false,
            error: error.message
          });
          break;
        }
      }

      results.push(routeResult);
    }

    setTestResults(results);
    setShowTestResults(true);
    setTesting(false);
  };

  const handleSaveConfiguration = async () => {
    if (!routeName) {
      toast.error('Please enter a route name');
      return;
    }

    // Validate at least primary route has hops
    if (routes.primary.hops.length === 0) {
      toast.error('Primary route must have at least one redirector');
      return;
    }

    const routeConfig = {
      name: routeName,
      projectId,
      primaryRoute: {
        ...routes.primary,
        hops: routes.primary.hops.map(h => ({
          redirectorId: h._id,
          hostname: h.hostname,
          ipAddress: h.ipAddress,
          type: h.type
        }))
      },
      fallbackRoutes: [
        routes.fallback1,
        routes.fallback2,
        routes.fallback3
      ]
        .filter(r => r.enabled && r.hops.length > 0)
        .map(r => ({
          ...r,
          hops: r.hops.map(h => ({
            redirectorId: h._id,
            hostname: h.hostname,
            ipAddress: h.ipAddress,
            type: h.type
          }))
        })),
      createdAt: new Date()
    };

    if (onSave) {
      onSave(routeConfig);
    } else {
      // Default behavior: save to localStorage
      const savedRoutes = JSON.parse(localStorage.getItem('routeConfigurations') || '[]');
      savedRoutes.push({ ...routeConfig, _id: Date.now().toString() });
      localStorage.setItem('routeConfigurations', JSON.stringify(savedRoutes));
      toast.success('Route configuration saved successfully');
    }
  };

  const handleExportJSON = () => {
    if (!routeName) {
      toast.error('Please enter a route name');
      return;
    }

    const exportData = {
      name: routeName,
      primaryRoute: routes.primary,
      fallbackRoutes: [routes.fallback1, routes.fallback2, routes.fallback3].filter(r => r.enabled)
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `route-${routeName.replace(/\s+/g, '-')}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Route configuration exported');
  };

  const handleClearAll = () => {
    if (window.confirm('Clear all routes? This cannot be undone.')) {
      setRoutes({
        primary: { priority: 1, enabled: true, hops: [], config: { maxRetries: 3, timeout: 10000, jitter: 20 } },
        fallback1: { priority: 2, enabled: false, hops: [], config: { maxRetries: 3, timeout: 10000, jitter: 20 } },
        fallback2: { priority: 3, enabled: false, hops: [], config: { maxRetries: 3, timeout: 10000, jitter: 20 } },
        fallback3: { priority: 4, enabled: false, hops: [], config: { maxRetries: 3, timeout: 10000, jitter: 20 } }
      });
      setRouteName('');
      toast.info('All routes cleared');
    }
  };

  const renderRedirectorCard = (redirector) => (
    <div
      key={redirector._id}
      draggable
      onDragStart={(e) => handleDragStart(e, redirector)}
      className="p-2 mb-2 border rounded"
      style={{
        cursor: 'grab',
        backgroundColor: redirector.health.isOnline ? '#f8f9fa' : '#fff3cd',
        borderColor: redirector.health.isOnline ? '#28a745' : '#ffc107'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <strong>{redirector.name}</strong>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            {redirector.hostname} ({redirector.type.toUpperCase()})
          </div>
        </div>
        <div className="text-end">
          <Badge bg={redirector.health.isOnline ? 'success' : 'warning'}>
            {redirector.health.isOnline ? 'Online' : 'Offline'}
          </Badge>
          {redirector.health.responseTime && (
            <div style={{ fontSize: '11px', color: '#6c757d' }}>
              {redirector.health.responseTime}ms
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderRouteVisualization = (routeKey) => {
    const route = routes[routeKey];
    const maxHops = 4; // Maximum hops allowed

    return (
      <div className="route-canvas p-4" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', minHeight: '300px' }}>
        <div className="d-flex align-items-center justify-content-center" style={{ gap: '10px' }}>
          {/* Agent */}
          <div className="text-center">
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#17a2b8',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px'
              }}
            >
              💻
            </div>
            <div style={{ fontSize: '12px', marginTop: '5px', fontWeight: 'bold' }}>Agent</div>
          </div>

          {/* Hops */}
          {Array.from({ length: maxHops }).map((_, index) => (
            <React.Fragment key={index}>
              <FontAwesomeIcon icon={faArrowRight} className="text-muted" />

              {route.hops[index] ? (
                // Filled hop
                <div className="text-center position-relative">
                  <div
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '8px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <div style={{ fontSize: '20px' }}>🔀</div>
                    <div style={{ fontSize: '10px', marginTop: '5px', textAlign: 'center', padding: '0 5px' }}>
                      {route.hops[index].name}
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute"
                      style={{ top: '-5px', right: '-5px', padding: '2px 6px', fontSize: '10px' }}
                      onClick={() => handleRemoveHop(routeKey, index)}
                    >
                      <FontAwesomeIcon icon={faTimesCircle} />
                    </Button>
                  </div>
                  <div style={{ fontSize: '11px', marginTop: '5px', color: '#6c757d' }}>
                    {route.hops[index].type.toUpperCase()}
                  </div>
                </div>
              ) : (
                // Empty drop zone
                <div
                  className="text-center"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, routeKey, index)}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '8px',
                    border: '2px dashed #ccc',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#999', textAlign: 'center' }}>
                    Drop<br/>Here
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}

          {/* C2 Server */}
          <FontAwesomeIcon icon={faArrowRight} className="text-muted" />
          <div className="text-center">
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#6610f2',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px'
              }}
            >
              🎯
            </div>
            <div style={{ fontSize: '12px', marginTop: '5px', fontWeight: 'bold' }}>C2</div>
          </div>
        </div>

        {route.hops.length > 0 && (
          <div className="text-center mt-3">
            <Badge bg="info">{route.hops.length} hop{route.hops.length !== 1 ? 's' : ''} configured</Badge>
            <Button variant="outline-danger" size="sm" className="ms-2" onClick={() => handleClearRoute(routeKey)}>
              <FontAwesomeIcon icon={faTrash} className="me-1" />
              Clear Route
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderRouteConfiguration = (routeKey) => {
    const route = routes[routeKey];

    return (
      <Card className="mt-3">
        <Card.Body>
          <h6>
            Route Configuration
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip>Fine-tune route behavior for reliability and stealth</Tooltip>}
            >
              <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ cursor: 'help' }} />
            </OverlayTrigger>
          </h6>

          <Form.Group className="mb-3">
            <Form.Label>
              Max Retries: {route.config.maxRetries}
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip>How many times to retry this route before trying next fallback (recommended: 3)</Tooltip>}
              >
                <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ cursor: 'help' }} />
              </OverlayTrigger>
            </Form.Label>
            <Form.Range
              min="1"
              max="10"
              value={route.config.maxRetries}
              onChange={(e) => handleConfigChange(routeKey, 'maxRetries', parseInt(e.target.value))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Timeout: {route.config.timeout / 1000}s
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip>How long to wait for response before timing out (recommended: 10s)</Tooltip>}
              >
                <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ cursor: 'help' }} />
              </OverlayTrigger>
            </Form.Label>
            <Form.Range
              min="5000"
              max="60000"
              step="1000"
              value={route.config.timeout}
              onChange={(e) => handleConfigChange(routeKey, 'timeout', parseInt(e.target.value))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Jitter: {route.config.jitter}%
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip>Random delay percentage to avoid pattern detection (recommended: 15-30%)</Tooltip>}
              >
                <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ cursor: 'help' }} />
              </OverlayTrigger>
            </Form.Label>
            <Form.Range
              min="0"
              max="50"
              value={route.config.jitter}
              onChange={(e) => handleConfigChange(routeKey, 'jitter', parseInt(e.target.value))}
            />
          </Form.Group>

          {routeKey !== 'primary' && (
            <Form.Check
              type="switch"
              label="Enable this fallback route"
              checked={route.enabled}
              onChange={() => handleToggleRoute(routeKey)}
            />
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderTestResults = () => {
    if (!testResults) return null;

    const successfulRoutes = testResults.filter(r => !r.skipped && r.success);
    const recommendedRoute = successfulRoutes.length > 0
      ? successfulRoutes.reduce((min, r) => r.totalLatency < min.totalLatency ? r : min, successfulRoutes[0])
      : null;

    return (
      <Modal show={showTestResults} onHide={() => setShowTestResults(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faFlask} className="me-2" />
            Route Test Results
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Route</th>
                <th>Priority</th>
                <th>Hops</th>
                <th>Total Latency</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {testResults.map((result) => {
                const isRecommended = recommendedRoute && result.routeKey === recommendedRoute.routeKey;

                return (
                  <tr key={result.routeKey} className={isRecommended ? 'table-success' : ''}>
                    <td>
                      <strong>
                        {result.routeKey === 'primary' ? 'Primary Route' : `Fallback ${result.routeKey.replace('fallback', '')}`}
                      </strong>
                      {isRecommended && (
                        <Badge bg="success" className="ms-2">Recommended</Badge>
                      )}
                    </td>
                    <td>
                      <Badge bg="secondary">Priority {result.priority}</Badge>
                    </td>
                    <td>
                      {result.skipped ? (
                        <span className="text-muted">{result.reason}</span>
                      ) : (
                        <div>
                          {result.hops.map((hop, idx) => (
                            <div key={idx} style={{ fontSize: '12px' }}>
                              <FontAwesomeIcon
                                icon={result.hopResults[idx]?.success ? faCheckCircle : faExclamationTriangle}
                                className={result.hopResults[idx]?.success ? 'text-success' : 'text-danger'}
                              />
                              {' '}{hop.name} ({result.hopResults[idx]?.latency || 0}ms)
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {result.skipped ? (
                        '-'
                      ) : (
                        <strong>{result.totalLatency}ms</strong>
                      )}
                    </td>
                    <td>
                      {result.skipped ? (
                        <Badge bg="secondary">Skipped</Badge>
                      ) : result.success ? (
                        <Badge bg="success">
                          <FontAwesomeIcon icon={faCheckCircle} /> Pass
                        </Badge>
                      ) : (
                        <div>
                          <Badge bg="danger">
                            <FontAwesomeIcon icon={faTimesCircle} /> Fail
                          </Badge>
                          {result.error && (
                            <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '5px' }}>
                              {result.error}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {recommendedRoute && (
            <Alert variant="success">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              <strong>Recommended Route:</strong> {recommendedRoute.routeKey === 'primary' ? 'Primary Route' : `Fallback ${recommendedRoute.routeKey.replace('fallback', '')}`} with lowest latency ({recommendedRoute.totalLatency}ms)
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestResults(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading redirectors...</p>
      </div>
    );
  }

  return (
    <>
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <FontAwesomeIcon icon={faRoute} className="me-2" />
              <strong>Route Builder</strong>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    Build multi-hop routes for your agents with automatic failover. Drag redirectors to create routes,
                    configure retry logic, and test before deployment.
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-white-50" style={{ cursor: 'help' }} />
              </OverlayTrigger>
            </div>
            <div>
              <Button
                variant="light"
                size="sm"
                className="me-2"
                onClick={handleTestAllRoutes}
                disabled={testing || routes.primary.hops.length === 0}
              >
                {testing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFlask} className="me-1" />
                    Test All Routes
                  </>
                )}
              </Button>
              <Button variant="success" size="sm" className="me-2" onClick={handleSaveConfiguration}>
                <FontAwesomeIcon icon={faSave} className="me-1" />
                Save
              </Button>
              <Button variant="info" size="sm" className="me-2" onClick={handleExportJSON}>
                <FontAwesomeIcon icon={faDownload} className="me-1" />
                Export JSON
              </Button>
              <Button variant="danger" size="sm" onClick={handleClearAll}>
                <FontAwesomeIcon icon={faTrash} className="me-1" />
                Clear All
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Route Name */}
          <Form.Group className="mb-3">
            <Form.Label>
              Route Name
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip>A descriptive name for this route configuration (e.g., "US East Multi-Hop")</Tooltip>}
              >
                <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ cursor: 'help' }} />
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter route name..."
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
            />
          </Form.Group>

          {redirectors.length === 0 ? (
            <Alert variant="warning">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              No redirectors available. Please deploy redirectors first using the Redirector Manager.
            </Alert>
          ) : (
            <Row>
              {/* Left Panel: Available Redirectors */}
              <Col md={3}>
                <h6>
                  Available Redirectors
                  <OverlayTrigger
                    placement="right"
                    overlay={<Tooltip>Drag redirectors from here to build your route</Tooltip>}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="ms-2 text-muted" style={{ cursor: 'help' }} />
                  </OverlayTrigger>
                </h6>
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {redirectors.map(renderRedirectorCard)}
                </div>
              </Col>

              {/* Center: Route Tabs and Visualization */}
              <Col md={9}>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-3"
                >
                  <Tab
                    eventKey="primary"
                    title={
                      <span>
                        <Badge bg="primary" className="me-2">1</Badge>
                        Primary Route
                        {routes.primary.hops.length > 0 && (
                          <Badge bg="success" className="ms-2">{routes.primary.hops.length} hops</Badge>
                        )}
                      </span>
                    }
                  >
                    <Alert variant="info">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                      The primary route is the first route the agent will try. Use the lowest-latency redirectors.
                    </Alert>
                    {renderRouteVisualization('primary')}
                    {renderRouteConfiguration('primary')}
                  </Tab>

                  <Tab
                    eventKey="fallback1"
                    title={
                      <span>
                        <Badge bg="secondary" className="me-2">2</Badge>
                        Fallback 1
                        {routes.fallback1.enabled && routes.fallback1.hops.length > 0 && (
                          <Badge bg="success" className="ms-2">{routes.fallback1.hops.length} hops</Badge>
                        )}
                      </span>
                    }
                  >
                    <Alert variant="warning">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                      Fallback routes are tried if the primary route fails. Enable and configure at least one fallback for resilience.
                    </Alert>
                    {renderRouteVisualization('fallback1')}
                    {renderRouteConfiguration('fallback1')}
                  </Tab>

                  <Tab
                    eventKey="fallback2"
                    title={
                      <span>
                        <Badge bg="secondary" className="me-2">3</Badge>
                        Fallback 2
                        {routes.fallback2.enabled && routes.fallback2.hops.length > 0 && (
                          <Badge bg="success" className="ms-2">{routes.fallback2.hops.length} hops</Badge>
                        )}
                      </span>
                    }
                  >
                    <Alert variant="warning">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                      Third priority route. Useful for different geographic regions or network paths.
                    </Alert>
                    {renderRouteVisualization('fallback2')}
                    {renderRouteConfiguration('fallback2')}
                  </Tab>

                  <Tab
                    eventKey="fallback3"
                    title={
                      <span>
                        <Badge bg="secondary" className="me-2">4</Badge>
                        Fallback 3
                        {routes.fallback3.enabled && routes.fallback3.hops.length > 0 && (
                          <Badge bg="success" className="ms-2">{routes.fallback3.hops.length} hops</Badge>
                        )}
                      </span>
                    }
                  >
                    <Alert variant="warning">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                      Last resort route. Maximum resilience configuration.
                    </Alert>
                    {renderRouteVisualization('fallback3')}
                    {renderRouteConfiguration('fallback3')}
                  </Tab>
                </Tabs>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Test Results Modal */}
      {renderTestResults()}
    </>
  );
};

export default RouteBuilder;
